import React, { useState } from 'react';
import { Percent, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { Modal } from '../../../components/Modal';
import { CurrencySelect } from '../../../components/CurrencySelect';
import { PAYMENT_APPS, getPaymentApp } from '../../../services/paymentLinks';
import type { Tab, Discount } from '../../../types';

interface TabSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    tab: Tab;
    onUpdate: (updates: Partial<Tab>) => Promise<void>;
    onDelete: () => void;
    onTransferOwnership: () => void;
    isOwner: boolean;
    onLeaveTab: () => void;
}

export const TabSettingsModal: React.FC<TabSettingsModalProps> = ({
    isOpen,
    onClose,
    tab,
    onUpdate,
    onDelete,
    onTransferOwnership,
    isOwner,
    onLeaveTab
}) => {
    const [name, setName] = useState(tab.name);
    const [currency, setCurrency] = useState(tab.currency);
    const [paymentAppId, setPaymentAppId] = useState(tab.paymentConfig?.appId ?? '');
    const [paymentUsername, setPaymentUsername] = useState(tab.paymentConfig?.username ?? '');
    const [tip, setTip] = useState(tab.tipPercentage.toString());
    const [service, setService] = useState(tab.serviceChargePercentage.toString());

    // Discounts State
    const [discounts, setDiscounts] = useState<Discount[]>(tab.discounts ?? []);
    const [newDiscountName, setNewDiscountName] = useState('');
    const [newDiscountType, setNewDiscountType] = useState<'fixed' | 'percent'>('percent');
    const [newDiscountValue, setNewDiscountValue] = useState('');
    const [newDiscountCategory, setNewDiscountCategory] = useState<'Food' | 'Drink' | 'Other' | 'All'>('All');

    // Reset form when modal opens or tab changes
    // Reset form when modal opens or tab changes - handlers handled by key-remount in parent
    // useEffect(() => { ... }) removed to fix lint error 'setState in effect'

    const handleAddDiscount = () => {
        if (!newDiscountName || !newDiscountValue) return;

        const newDiscount: Discount = {
            id: Math.random().toString(36).substring(2, 9),
            name: newDiscountName,
            type: newDiscountType,
            value: parseFloat(newDiscountValue),
            ...(newDiscountCategory !== 'All' ? { category: newDiscountCategory } : {})
        };

        setDiscounts([...discounts, newDiscount]);

        // Reset inputs
        setNewDiscountName('');
        setNewDiscountValue('');
        setNewDiscountType('percent');
        setNewDiscountCategory('All');
    };

    const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        void onUpdate({
            name,
            currency,
            paymentConfig: paymentAppId && paymentUsername
                ? { appId: paymentAppId, username: paymentUsername }
                : null,
            tipPercentage: parseFloat(tip) || 0,
            serviceChargePercentage: parseFloat(service) || 0,
            discounts: discounts
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Tab Settings">
            <form onSubmit={handleSubmit} className="space-y-4">
                <Input label="Tab Name" value={name} onChange={(e) => { setName(e.target.value); }} />

                <CurrencySelect
                    label="Currency"
                    value={currency}
                    onChange={setCurrency}
                />

                {/* Payment App Selector */}
                <Select
                    label="Payment App"
                    value={paymentAppId}
                    onChange={(e) => {
                        setPaymentAppId(e.target.value);
                        if (!e.target.value) setPaymentUsername('');
                    }}
                    options={[
                        { value: '', label: 'None (no payment link)' },
                        ...PAYMENT_APPS.map((app) => ({
                            value: app.id,
                            label: `${app.icon}  ${app.name}`,
                        })),
                    ]}
                />
                {paymentAppId && (
                    <Input
                        label={paymentAppId === 'custom' ? 'Payment URL' : 'Username / Handle'}
                        value={paymentUsername}
                        onChange={(e) => { setPaymentUsername(e.target.value); }}
                        placeholder={
                            getPaymentApp(paymentAppId)?.placeholder ?? 'Enter username'
                        }
                    />
                )}
                <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                        <Input
                            label="Tip %"
                            type="number"
                            value={tip}
                            onChange={(e) => { setTip(e.target.value); }}
                        />
                        <Percent size={14} className="absolute right-3 top-9 text-text-faint" />
                    </div>
                    <div className="relative">
                        <Input
                            label="Service %"
                            type="number"
                            value={service}
                            onChange={(e) => { setService(e.target.value); }}
                        />
                        <Percent size={14} className="absolute right-3 top-9 text-text-faint" />
                    </div>
                </div>

                {/* Discounts Section */}
                <div className="pt-2 border-t border-border-default">
                    <label className="text-sm font-semibold text-text-primary mb-2 block">Discounts</label>
                    <div className="space-y-3">
                        {discounts.map((discount, index) => (
                            <div key={index} className="flex items-center gap-2 bg-bg-surface p-2 rounded-lg border border-border-default">
                                <div className="flex-1">
                                    <div className="font-medium text-sm">{discount.name}</div>
                                    <div className="text-xs text-text-muted">
                                        {discount.type === 'fixed' ? '£' : ''}{discount.value}{discount.type === 'percent' ? '%' : ''} off {discount.category ?? 'Everything'}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setDiscounts(discounts.filter((_, i) => i !== index)); }}
                                    className="p-1.5 text-text-muted hover:text-red-500 hover:bg-bg-base rounded-md"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}

                        {/* Add Discount Form */}
                        <div className="bg-bg-base p-3 rounded-lg border border-border-default space-y-3">
                            <div className="text-xs font-semibold text-text-muted uppercase">Add Discount</div>
                            <Input
                                placeholder="Name (e.g. 50% Off Food)"
                                value={newDiscountName}
                                onChange={(e) => { setNewDiscountName(e.target.value); }}
                                className="h-9 text-sm"
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <select
                                    className="h-9 rounded-lg border border-border-default bg-bg-surface px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-base"
                                    value={newDiscountType}
                                    onChange={(e) => { setNewDiscountType(e.target.value as 'fixed' | 'percent'); }}
                                >
                                    <option value="percent">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount (£)</option>
                                </select>
                                <Input
                                    type="number"
                                    placeholder="Value"
                                    value={newDiscountValue}
                                    onChange={(e) => { setNewDiscountValue(e.target.value); }}
                                    className="h-9 text-sm"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <select
                                    className="h-9 rounded-lg border border-border-default bg-bg-surface px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-base"
                                    value={newDiscountCategory}
                                    onChange={(e) => { setNewDiscountCategory(e.target.value as 'Food' | 'Drink' | 'Other' | 'All'); }}
                                >
                                    <option value="All">All Items</option>
                                    <option value="Food">Food Only</option>
                                    <option value="Drink">Drinks Only</option>
                                    <option value="Other">Other Only</option>
                                </select>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="h-9"
                                    onClick={handleAddDiscount}
                                    disabled={!newDiscountName || !newDiscountValue}
                                >
                                    <Plus size={14} className="mr-1" /> Add
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pt-2 space-y-3">
                    <Button type="submit" className="w-full h-12">Save Changes</Button>
                    {isOwner ? (
                        <>
                            <Button
                                variant="ghost"
                                className="w-full text-red-500 font-bold"
                                type="button"
                                onClick={onDelete}
                            >
                                Delete Tab
                            </Button>
                            <div className="pt-4 border-t border-border-default mt-4">
                                <Button
                                    variant="outline"
                                    className="w-full border-border-default text-text-muted"
                                    type="button"
                                    onClick={onTransferOwnership}
                                >
                                    Transfer Ownership
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Button
                            variant="ghost"
                            className="w-full text-red-500 font-bold"
                            type="button"
                            onClick={onLeaveTab}
                        >
                            Leave Tab
                        </Button>
                    )}
                </div>
            </form>
        </Modal>
    );
};
